import React, {useMemo, useRef, useState} from 'react';
import {
  Box,
  TextareaAutosize,
  TextareaAutosizeProps,
  Typography,
} from '@mui/material';
import './TextAreaComponent.scss';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from './../../../../../assets/images/info-icon.svg';
import CreateJobsFieldsWrapper from '../case-context-container/CreateJobsFieldsWrapper';
import ArrBanner from '../../../../common/arr-banner/ArrBanner';
import {ReactComponent as SuccessIcon} from '../../../../../../src/assets/images/success-icon.svg';
import {ReactComponent as ResetIcon} from '../../../../../../src/assets/images/icon-reset.svg';
import {t} from 'i18next';

export interface MultilineTextInputProps
  extends Omit<TextareaAutosizeProps, 'onChange'> {
  value: string;
  onChange: (newValue: string) => void;
  label?: string;
  isRequired?: boolean;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  maxRows?: number;
  isResponsive?: boolean;
  ariaLabel?: string;
  helperText?: string;
  maxLength?: number;
  resizable?: boolean;
  className?: string;
  displayOptionalLabel?: boolean;
  displayArrTooltip?: boolean;
  toolTipTitle?: string;
  toolTipContent?: string;
  displayHandle?: boolean;
  childrenClassName?: string;
  MIN_HEIGHT?: number;
  MAX_HEIGHT?: number;
  IsDefaultValueMinHeight?: boolean;
  enableBanner?: boolean;
  isEditMode?: boolean;
  bannerContent?: string;
  bannerAction?: () => void;
}

const TextAreaComponent: React.FC<MultilineTextInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  isRequired = false,
  disabled = false,
  rows,
  maxRows = 8,
  ariaLabel,
  helperText,
  maxLength = 500,
  resizable = false,
  className = '',
  displayOptionalLabel = true,
  displayArrTooltip = false,
  toolTipTitle = '',
  toolTipContent = '',
  displayHandle = false,
  childrenClassName = '',
  MIN_HEIGHT = 256,
  MAX_HEIGHT = 560,
  IsDefaultValueMinHeight,
  enableBanner = false,
  isEditMode = false,
  bannerContent = '',
  bannerAction,
  ...rest
}) => {
  const sanitizedLabel = label?.replaceAll(' ', '-');
  const helperTextId = helperText ? `${sanitizedLabel}-helper-text` : undefined;
  const charCounterId = `${sanitizedLabel}-char-counter`;
  const ariaDescribedBy = useMemo(
    () => [helperTextId, charCounterId].filter(Boolean).join(' '),
    [helperTextId, charCounterId]
  );
  const currentCharCount = value?.length || 0;
  return (
    <>
      <CreateJobsFieldsWrapper
        resizable={resizable}
        displayHandle={displayHandle}
        MIN_HEIGHT={MIN_HEIGHT}
        MAX_HEIGHT={MAX_HEIGHT}
        header={
          label ? (
            <>
              <Typography
                className="textarea-label"
                data-testid="textarea-label"
                aria-label={label}
                tabIndex={0}
              >
                {label}
                {!isRequired ? (
                  <span
                    className="optional-text"
                    data-testid={`text-area-component-diplay-label-not-required-${label.replaceAll(' ', '-')}`}
                  >
                    {displayOptionalLabel ? ' (optional)' : ''}
                  </span>
                ) : (
                  <span
                    className="required-text"
                    data-testid={`text-area-component-diplay-label-required-${label.replaceAll(' ', '-')}`}
                  >
                    {'*'}
                  </span>
                )}
              </Typography>
              {displayArrTooltip && (
                <ArrTooltipWrapper
                  title={toolTipTitle}
                  aria-label={toolTipTitle}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      fontSize="medium"
                      aria-label="information"
                      data-testid={'toolTipTitle'}
                      tabIndex={0}
                    />
                  }
                >
                  <Box aria-label={toolTipContent} tabIndex={0}>
                    {toolTipContent}
                  </Box>
                </ArrTooltipWrapper>
              )}
            </>
          ) : null
        }
        className={`textarea-wrapper ${resizable ? 'resizable' : ''} ${className}`}
        childrenClassName={childrenClassName}
        IsDefaultValueMinHeight={IsDefaultValueMinHeight}
      >
        <Box className="text-area-banner-wrapper">
          {enableBanner && (
            <ArrBanner
              variant={isEditMode ? 'info' : 'success'}
              className="banner-class"
              bannerContent={bannerContent}
              type="contextual"
              startIcon={isEditMode ? <InfoIcon /> : <SuccessIcon />}
              actions={
                isEditMode
                  ? [
                      {
                        label: 'Reset',
                        startIcon: <ResetIcon />,
                        onClick: () => bannerAction?.(),
                        dataTestId: 'reset-action',
                      },
                    ]
                  : []
              }
              dataTestId="text-area-banner"
            />
          )}
          <TextareaAutosize
            className="textarea-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || ''}
            disabled={disabled}
            minRows={rows}
            maxRows={maxRows}
            maxLength={maxLength}
            aria-label={ariaLabel || label || 'Multiline Text Input'}
            aria-describedby={ariaDescribedBy}
            data-testid="textarea-input"
            {...rest}
          />
          <Typography
            id={charCounterId}
            className="sr-only"
            data-testid="textarea-char-counter"
          >
            {currentCharCount} of {maxLength}
            {t('ai.review.newJobDetail.textAreaCharUsed')}
          </Typography>
          {helperText && (
            <Typography
              className="textarea-helper"
              data-testid="textarea-helper"
              aria-label={helperText}
            >
              {helperText}
            </Typography>
          )}
        </Box>
      </CreateJobsFieldsWrapper>
    </>
  );
};

export default TextAreaComponent;
